import { computed, defineComponent, ref, watch, nextTick, } from '@vue/composition-api';
import { AgGridVue } from 'ag-grid-vue';
const AG_GRID_CONSTANTS = {
    HEADER_HEIGHT: 35,
    FILTER_HEIGHT: 35,
    ROW_HEIGHT: 35,
    BORDER_ADJUSTMENT: 1,
};
const CELL_STYLE = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    height: '100%',
};
class AGTableGeneric {
    define() {
        return defineComponent({
            components: {
                AgGridVue,
            },
            props: {
                rows: {
                    type: Array,
                    default: () => [],
                },
                columns: {
                    type: Array,
                    required: true,
                },
                pinnedBottomRowData: {
                    type: Array,
                    default: () => [],
                },
                getRowClass: {
                    type: Function,
                },
                getRowId: {
                    type: Function,
                },
                serverSideFiltering: {
                    type: Boolean,
                    default: false,
                },
                serverSideSorting: {
                    type: Boolean,
                    default: false,
                },
                maxHeight: {
                    type: String,
                    default: null,
                },
                borderAdjustment: {
                    type: Number,
                    default: AG_GRID_CONSTANTS.BORDER_ADJUSTMENT,
                },
                rowHeight: {
                    type: Number,
                    default: AG_GRID_CONSTANTS.ROW_HEIGHT,
                },
                selectable: {
                    type: Boolean,
                    default: false,
                },
                gridOptions: {
                    type: Object,
                    default: () => ({}),
                },
            },
            setup(props, ctx) {
                const agGridRef = ref(null);
                const gridApi = ref(null);
                const displayedRowCount = ref(props.rows.length);
                const defaultColDef = computed(() => ({
                    suppressHeaderMenuButton: true,
                    sortable: props.serverSideSorting || true,
                    suppressMovable: true,
                }));
                const columnsWithSelection = computed(() => {
                    if (!props.selectable) {
                        return props.columns;
                    }
                    const checkboxColumn = {
                        headerName: '',
                        checkboxSelection: true,
                        headerCheckboxSelection: true,
                        width: 67,
                        sortable: false,
                        filter: false,
                        resizable: false,
                        suppressHeaderMenuButton: true,
                        suppressMovable: true,
                        pinned: 'left',
                        lockPosition: true,
                        cellStyle: CELL_STYLE,
                    };
                    return [checkboxColumn, ...props.columns];
                });
                const tableStyle = computed(() => {
                    const hasFilters = columnsWithSelection.value.some((column) => column.floatingFilter);
                    let base = AG_GRID_CONSTANTS.HEADER_HEIGHT;
                    if (hasFilters)
                        base += AG_GRID_CONSTANTS.FILTER_HEIGHT;
                    if (props.pinnedBottomRowData)
                        base += props.pinnedBottomRowData.length * props.rowHeight;
                    const totalHeight = base +
                        displayedRowCount.value * props.rowHeight +
                        props.borderAdjustment;
                    return {
                        height: `${totalHeight}px`,
                        maxHeight: props.maxHeight || '100%',
                    };
                });
                const handleRowClick = ({ data }) => {
                    ctx.emit('row-clicked', data);
                };
                const handleRowHover = (params) => {
                    ctx.emit('row-hover-start', params.data);
                };
                const handleSortChanged = (event) => {
                    const sortedColumn = event.api
                        .getColumnState()
                        .find((col) => Boolean(col?.sort));
                    if (sortedColumn) {
                        const { colId, sort } = sortedColumn;
                        if (colId && sort) {
                            ctx.emit('sort-changed', colId, sort);
                        }
                    }
                    else {
                        ctx.emit('reset-sort');
                    }
                };
                const handleFilterChange = (event) => {
                    if (props.serverSideFiltering) {
                        const filterModel = event.api.getFilterModel();
                        ctx.emit('filter-changed', filterModel);
                    }
                    else {
                        displayedRowCount.value = event.api.getDisplayedRowCount();
                        const filterModel = event.api.getFilterModel();
                        ctx.emit('filter-changed', filterModel);
                    }
                };
                const handleSelectionChanged = (event) => {
                    const selectedRows = event.api.getSelectedRows();
                    ctx.emit('selection-changed', selectedRows);
                };
                const onGridReady = (event) => {
                    gridApi.value = event.api;
                    ctx.emit('grid-ready', event);
                };
                const refreshGrid = async () => {
                    if (!gridApi.value) {
                        console.warn('Grid API not ready yet');
                        return;
                    }
                    try {
                        await nextTick();
                        if (typeof gridApi.value.setRowData === 'function') {
                            gridApi.value.setRowData(props.rows);
                        }
                    }
                    catch (error) {
                        console.error('Error refreshing AG-Grid:', error);
                    }
                };
                watch(() => props.rows, async (newRows) => {
                    displayedRowCount.value = newRows.length;
                    await refreshGrid();
                }, { deep: true, immediate: true });
                return {
                    agGridRef,
                    displayedRowCount,
                    columnsWithSelection,
                    handleRowClick,
                    handleRowHover,
                    handleSortChanged,
                    tableStyle,
                    handleFilterChange,
                    handleSelectionChanged,
                    onGridReady,
                    defaultColDef,
                    AG_GRID_CONSTANTS,
                    gridApi,
                    refreshGrid,
                    currentRowHeight: props.rowHeight,
                };
            },
        });
    }
}
const main = new AGTableGeneric().define();
export function GenericAGTable() {
    return main;
}
export default main;
